import * as THREE from 'three';
import type { SketchFace, SketchVec3 } from './types';

/**
 * Face triangulation — render-time derivation, never stored.
 *
 * A face's analytic loops are projected onto the face plane (U = (p-origin)·u,
 * V = (p-origin)·v), triangulated with THREE.ShapeUtils (earcut with holes),
 * then lifted back to world coordinates with the face plane's shared normal.
 * The face normal is emitted flat for every triangle: Sketch faces are
 * perfectly planar and per-vertex smoothing would lie about edges.
 */

/** Orthonormal in-plane basis for a unit normal (stable, no branching on axis). */
export function planeBasis(normal: THREE.Vector3): { u: THREE.Vector3; v: THREE.Vector3 } {
    const n = normal.clone().normalize();
    // Pick the reference axis least aligned with the normal for stability.
    const ref = Math.abs(n.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(ref, n).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    return { u, v };
}

/** Project world points of a planar loop into the face plane's 2D space. */
export function projectLoop(points: SketchVec3[], origin: THREE.Vector3, u: THREE.Vector3, v: THREE.Vector3): THREE.Vector2[] {
    return points.map((p) => {
        const d = new THREE.Vector3(...p).sub(origin);
        return new THREE.Vector2(d.dot(u), d.dot(v));
    });
}

/**
 * Triangulate one face into a world-space BufferGeometry (positions + flat
 * normals, non-indexed). Returns null for degenerate loops (<3 points).
 */
export function faceToGeometry(face: SketchFace): THREE.BufferGeometry | null {
    if (face.outer.length < 3) return null;

    const normal = new THREE.Vector3(...face.normal).normalize();
    const origin = new THREE.Vector3(...face.origin);
    const { u, v } = planeBasis(normal);

    const contour = projectLoop(face.outer, origin, u, v);
    const holes = face.holes
        .filter((h) => h.length >= 3)
        .map((h) => projectLoop(h, origin, u, v));

    let triangles: number[][];
    try {
        triangles = THREE.ShapeUtils.triangulateShape(contour, holes);
    } catch {
        // Degenerate contours (self-intersections from a repaired mesh):
        // fall back to an ear fan of the outer contour so the face stays
        // visible instead of vanishing from the shell.
        triangles = [];
        for (let i = 1; i < face.outer.length - 1; i++) triangles.push([0, i, i + 1]);
    }
    if (triangles.length === 0) return null;

    // Lift triangulated 2D vertices back to world space (dedup by index list
    // is unnecessary: triangulateShape returns indices into the combined
    // contour+hole vertex pool, which we expand directly).
    const pool = [face.outer, ...face.holes.filter((h) => h.length >= 3)];
    const positions = new Float32Array(triangles.length * 3 * 3);
    let w = 0;
    for (const tri of triangles) {
        for (const idx of tri) {
            // Walk the pool to find which loop holds this vertex index.
            let remaining = idx;
            let loop = 0;
            while (loop < pool.length && remaining >= pool[loop].length) {
                remaining -= pool[loop].length;
                loop++;
            }
            const p = pool[loop]?.[remaining] ?? face.outer[0];
            positions[w++] = p[0];
            positions[w++] = p[1];
            positions[w++] = p[2];
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const normals = new Float32Array(triangles.length * 3 * 3);
    for (let i = 0; i < triangles.length * 3; i++) {
        normals[i * 3] = normal.x;
        normals[i * 3 + 1] = normal.y;
        normals[i * 3 + 2] = normal.z;
    }
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    geometry.computeBoundingSphere();
    return geometry;
}

/** Dense polyline (LineLoop-ready points) of a face contour in world space. */
export function faceContourPoints(face: SketchFace): THREE.Vector3[] {
    return face.outer.map((p) => new THREE.Vector3(...p));
}
